import type { DocumentsConfig } from '@/api/generated';
import { inMegabytes } from '@/lib/images';

import { typeWord } from './documentLabels';

/**
 * What the upload form may accept, read from `GET /config` and nowhere else
 * (P8, I6). The limit is a deployment setting and the types are the
 * backend's, so nothing here holds a number or a media type of its own; the
 * test reads this file to make sure of it.
 *
 * None of this is a guard: the backend judges the bytes. It is here so
 * somebody who picks the wrong file is told at once, not after an upload
 * that ends in a 415.
 */
export interface UploadPolicy {
  maxUploadMb: number;
  maxUploadBytes: number;
  acceptedTypes: string[];
}

export function policyFrom(config: DocumentsConfig): UploadPolicy {
  return {
    maxUploadMb: config.max_upload_mb,
    maxUploadBytes: config.max_upload_bytes,
    acceptedTypes: [...config.accepted_types],
  };
}

/** The `accept` attribute of the file input. */
export function acceptAttribute(policy: UploadPolicy): string {
  return policy.acceptedTypes.join(',');
}

/** "PDF, PNG or JPEG": the types as a person reads them. */
export function typesInWords(policy: UploadPolicy): string {
  const words = policy.acceptedTypes.map(typeWord);
  if (words.length <= 1) return words.join('');
  return `${words.slice(0, -1).join(', ')} or ${words[words.length - 1]}`;
}

/** Why a file is refused before it is sent, or null when it may go. */
export function refuseFile(file: File, policy: UploadPolicy): string | null {
  if (!policy.acceptedTypes.includes(file.type)) {
    return `Choose a ${typesInWords(policy)} file.`;
  }
  if (file.size > policy.maxUploadBytes) {
    return `That file is ${inMegabytes(file.size)}. Use one of ${inMegabytes(policy.maxUploadBytes)} or less.`;
  }
  return null;
}
